import { FaroTable } from "@components/common/faro-table/faro-table";
import { GroupBulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { GroupHeaders, GROUPS_SORT_BY_MAP } from "@components/groups/groups-constants";
import { getGroupsColumns } from "@components/table/groups/groups-columns";
import { getSortByFromGridSortModel } from "@components/table/groups/groups-table-utils";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { GroupsSortBy, GroupTypes } from "@custom-types/group-types";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useDateTime } from "@hooks/use-date-time";
import { useMediaQueryList } from "@hooks/use-media-query";
import { GridSortModel } from "@mui/x-data-grid-pro";
import { useAppDispatch } from "@store/store-helper";
import { resetTableState, setTableType } from "@store/table/table-slice";
import { TableEvents, TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useMemo } from "react";

interface Props extends BaseCompanyIdProps {
  /** The groups that is shown in the table */
  groups: GroupTypes[];

  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** All the columns that we want to show. The order defines the order of the columns */
  requiredColumns: GroupHeaders[];

  /** List of all the bulk actions and callbacks */
  bulkActionButtons?: GroupBulkButtonComponents;

  /** Groups sort criteria */
  sortBy: GroupsSortBy;

  /** Callback to update the sort criteria */
  setSortBy?: (sortBy: GroupsSortBy) => void;
}

/** Listing all the groups of a company */
export function GroupsTable({
  companyId,
  groups,
  isLoading,
  requiredColumns,
  bulkActionButtons,
  sortBy,
  setSortBy,
}: Props): JSX.Element {
  const { navigateToGroupDetails } = useAppNavigation();
  const dispatch = useAppDispatch();
  const { isScreenMd, isScreenLgOrLarger } = useMediaQueryList();
  const { formatDate } = useDateTime();
  const { trackEvent } = useTrackEvent();

  /** Convert the sortBy prop to a GridSortModel for the table UI */
  const sortModel = useMemo(() => [GROUPS_SORT_BY_MAP[sortBy]], [sortBy]);

  // Callback fired when the sort option changes in list view
  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      const newSortBy = getSortByFromGridSortModel(newModel);

      // Track the sort event
      trackEvent({
        name: TableEvents.sortItems,
        props: {
          subject: "Group",
          sortBy: GROUPS_SORT_BY_MAP[newSortBy].field,
          sortOrder: GROUPS_SORT_BY_MAP[newSortBy].sort,
          viewMode: "list",
        },
      });

      if (!setSortBy) {
        return;
      }
      
      setSortBy(newSortBy);
    },
    [setSortBy, trackEvent]
  );

  // Get sortable columns
  const sortableColumns = useMemo(() => {
    return requiredColumns.map((requiredColumn) => {
      const column = getGroupsColumns({
        options: {
          isScreenLgOrLarger: isScreenLgOrLarger,
          isScreenMd,
        },
        formatDate,
      })[requiredColumn];

      // Enable sorting for specific columns (sorting is handled client-side in parent component)
      const isSortableColumn = [
        GroupHeaders.name,
        GroupHeaders.members,
        GroupHeaders.projects,
        GroupHeaders.createdDate,
      ].includes(requiredColumn);

      return {
        ...column,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        sortable: isSortableColumn,
      };
    });
  }, [requiredColumns, isScreenLgOrLarger, isScreenMd, formatDate]);

  // Make sure the correct table type is set in the store and reset the bulk action state after unmounting
  useEffect(() => {
    dispatch(setTableType(TableType.groups));

    return () => {
      dispatch(resetTableState());
    };
  }, [dispatch]);

  return (
    <FaroTable
      entities={groups}
      uniqueIdKey={"id"}
      isLoading={isLoading}
      columns={sortableColumns}
      sx={{
        "& .MuiDataGrid-row": {
          cursor: "pointer",
        },
      }}
      onRowClick={(groupId) =>
        navigateToGroupDetails({
          companyId,
          groupId,
        })
      }
      bulkActionButtons={bulkActionButtons}
      rowHeight={70}
      tableType={TableType.groups}
      defaultSortModel={sortModel}
      onSortModelChange={handleSortModelChange}
    />
  );
}
